import noMaintenancesImg from "assets/img/no-documents.svg"

import { FC, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { uniq } from "lodash"

import Button from "components/Button/Button"
import { Flex, FlexItem } from "components/Flex"
import Select from "components/Select/Select"
import Spinner from "components/Spinner/Spinner"
import useAssetMaintenances from "hooks/useAssetMaintenances"
import useMaintenanceTypes from "hooks/useMaintenanceTypes"
import { Guid, IApiLink } from "@/types"
import MaintenanceEditModal from "../MaintenanceEditModal/MaintenanceEditModal"
import { Grid, GridItem } from "components/Grid"
import Text from "components/Text/Text"
import AssetMaintenancesItem from "./AssetMaintenancesItem"

interface IAssetMaintenancesProps {
    assetId: Guid,
    assetOperationHours?: number | null
    linkCreateAssetMaintenance?: IApiLink
    hideTitle?: boolean
}

const AssetMaintenances: FC<IAssetMaintenancesProps> = ({
    assetId,
    assetOperationHours,
    linkCreateAssetMaintenance,
    hideTitle = false,
}) => {
    const { t } = useTranslation()
    const [showScheduleMaintenanceModal, setShowScheduleMaintenanceModal] = useState(false)
    const [selectedMaintenanceTypeId, setSelectedMaintenanceTypeId] = useState<Guid | "0">("0")

    const {
        maintenances,
        refetchMaintenances,
    } = useAssetMaintenances({
        assetId,
        pageSize: 1000,
        includeCompleted: true,
    })

    const currentMaintenanceTypeIds = useMemo(() => {
        const typeIds = maintenances?.map(({ maintenance }) => maintenance.typeId)
        return typeIds ? uniq(typeIds) : []
    }, [maintenances])

    const maintenancesFiltered = useMemo(() => {
        if (selectedMaintenanceTypeId === "0" || !maintenances) return maintenances
        return maintenances?.filter(({ maintenance }) => maintenance.typeId === selectedMaintenanceTypeId)
    }, [maintenances, selectedMaintenanceTypeId])

    const maintenancesUpcoming = useMemo(() => {
        if (!maintenancesFiltered) return []
        return maintenancesFiltered.filter(({ maintenance }) => !maintenance.completedDate)
    }, [maintenancesFiltered])

    const maintenancesCompleted = useMemo(() => {
        if (!maintenancesFiltered) return []
        return maintenancesFiltered.filter(({ maintenance }) => maintenance.completedDate)
    }, [maintenancesFiltered])

    const {
        maintenanceTypes,
        maintenanceTypeSelectOptions,
        fetchMaintenanceTypes,
    } = useMaintenanceTypes()

    useEffect(() => {
        if (maintenanceTypes) return
        fetchMaintenanceTypes?.()
    }, [fetchMaintenanceTypes, maintenanceTypes])

    useEffect(() => {
        if (!currentMaintenanceTypeIds.includes(selectedMaintenanceTypeId)) {
            setSelectedMaintenanceTypeId("0")
        }
    }, [currentMaintenanceTypeIds, selectedMaintenanceTypeId])

    if (!maintenances) {
        return <Spinner className="py-8" />
    }

    function renderSheduleNewMaintenanceButton() {
        if (!linkCreateAssetMaintenance) return null
        return (
            <Button
                theme="secondary"
                label={t('maintenance.scheduleNewMaintenance')}
                onClick={() => setShowScheduleMaintenanceModal(true)} />
        )
    }

    const scheduleNewMaintenanceButton = renderSheduleNewMaintenanceButton()

    return (
        <>
            {maintenances.length === 0
                ? <div className="text-center py-8">
                    <img src={noMaintenancesImg} alt="" className="mb-4" />
                    <div>{t('maintenance.noMaintenances')}</div>
                    {!!linkCreateAssetMaintenance &&
                        <>
                            <div className="mb-4">{t('maintenance.wantScheduleMaintenance')}</div>
                            {scheduleNewMaintenanceButton}
                        </>
                    }
                </div>
                : <>
                    <Flex alignItems="center" justifyContent="space-between" className="mb-4">
                        <FlexItem>
                            {scheduleNewMaintenanceButton}
                        </FlexItem>
                        <FlexItem>
                            <Select
                                value={selectedMaintenanceTypeId}
                                options={[
                                    { label: `${t("maintenance.maintenanceTypes")}: ${t("filter.all")}`, value: "0" },
                                    ...(maintenanceTypeSelectOptions?.filter(o => currentMaintenanceTypeIds.includes(o.value)) || []),
                                ]}
                                onChange={option => setSelectedMaintenanceTypeId(option?.value || "0")}
                            />
                        </FlexItem>
                    </Flex>
                    <Grid gap="3.2rem">
                        {maintenancesUpcoming.length > 0 &&
                            <GridItem>
                                <Text type="h3" className="mb-2">{t('maintenance.upcomingMaintenance')}</Text>
                                {maintenancesUpcoming.map(({ maintenance }) => (
                                    <AssetMaintenancesItem
                                        key={maintenance.id}
                                        maintenance={maintenance}
                                        assetOperationHours={assetOperationHours}
                                        onChanged={refetchMaintenances}
                                    />
                                ))}
                            </GridItem>
                        }
                        {maintenancesCompleted.length > 0 &&
                            <GridItem>
                                <Text type="h3" className="mb-2">{t('maintenance.completedMaintenance')}</Text>
                                {maintenancesCompleted.map(({ maintenance }) => (
                                    <AssetMaintenancesItem
                                        key={maintenance.id}
                                        maintenance={maintenance}
                                        assetOperationHours={assetOperationHours}
                                        onChanged={refetchMaintenances}
                                    />
                                ))}
                            </GridItem>
                        }
                    </Grid>
                </>
            }
            {showScheduleMaintenanceModal &&
                <MaintenanceEditModal
                    linkCreateAssetMaintenance={linkCreateAssetMaintenance}
                    onClose={() => setShowScheduleMaintenanceModal(false)}
                    onChanged={refetchMaintenances}
                />
            }
        </>
    )
}

export default AssetMaintenances
